import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import {
  DIVISION_COLORS,
  getEcosystemItems,
  ITEM_TYPE_LABELS,
  PURCHASE_ACTIONS,
  RELATIONSHIP_LABELS,
} from '@/data/ecosystem';
import { useLiveTick } from '@/lib/liveContent';
import { SITE_ORIGIN, useSeo } from '@/lib/seo';
import { CtaBand } from '@/components/site/CtaBand';

const enquiryHref = (item) => {
  const division = ['operations', 'people', 'growth', 'launch'].includes(item.serviceDivision)
    ? `service=${item.serviceDivision}&`
    : '';
  return `/?${division}source=Ecosystem#solution-finder`;
};

export default function EcosystemServicePage() {
  const { slug } = useParams();
  useLiveTick();
  const items = getEcosystemItems();
  const item = items.find((i) => i.slug === slug);
  const origin = SITE_ORIGIN;

  useSeo(
    item
      ? {
          title: `${item.name} | Amden Group`,
          description: item.shortDescription,
          canonical: `${origin}/ecosystem/services/${item.slug}`,
          ogImage: `${origin}/assets/amden-logo-dark.png`,
        }
      : {
          title: 'Service not found | Amden Group',
          description: 'This catalogue entry could not be found.',
        }
  );

  if (!item) {
    return (
      <section className="bg-mist py-24 sm:py-32" data-testid="eco-service-not-found">
        <div className="mx-auto max-w-2xl px-4 text-center sm:px-6">
          <h1 className="text-3xl font-extrabold tracking-tight text-navy-900">Service not found</h1>
          <Link
            to="/ecosystem"
            className="group mt-8 inline-flex items-center gap-2 rounded-md bg-navy-900 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-navy-700"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to the catalogue
          </Link>
        </div>
      </section>
    );
  }

  const division = DIVISION_COLORS[item.serviceDivision] || DIVISION_COLORS.amden;
  const action = PURCHASE_ACTIONS[item.purchaseMode] || PURCHASE_ACTIONS.contact_us;
  const actionHref = action.kind === 'details' ? enquiryHref(item) : enquiryHref(item);
  const related = items.filter(
    (i) => i.serviceDivision === item.serviceDivision && i.id !== item.id
  ).slice(0, 3);

  const rows = [
    { label: 'Who it’s for', value: item.suitableFor },
    { label: 'Delivery format', value: item.format },
    { label: 'Delivery timescale', value: item.deliveryTimescale },
    { label: 'Price', value: item.priceDisplay },
  ].filter((r) => r.value);

  return (
    <>
      <section data-testid="eco-service-page" className="bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <Link
            to="/ecosystem"
            data-testid="eco-service-back"
            className="group inline-flex items-center gap-2 text-sm font-bold text-navy-900 transition-colors hover:text-gold-700"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" aria-hidden="true" />
            Back to the catalogue
          </Link>

          <div className="mt-8 flex flex-wrap items-center gap-2">
            <span
              className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
              style={{ backgroundColor: division.soft, color: division.dark }}
            >
              {ITEM_TYPE_LABELS[item.itemType]}
            </span>
            <span className="rounded-full bg-navy-900 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
              {RELATIONSHIP_LABELS[item.relationship]}
            </span>
            <span className="rounded-full border border-slate-200 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
              {division.label}
            </span>
          </div>

          <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl" data-testid="eco-service-heading">
            {item.name}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">{item.shortDescription}</p>

          {rows.length > 0 && (
            <dl className="mt-8 grid gap-4 rounded-2xl border border-slate-200 bg-mist p-6 sm:grid-cols-2 sm:p-8">
              {rows.map((row) => (
                <div key={row.label}>
                  <dt className="text-xs font-bold uppercase tracking-wide text-slate-500">{row.label}</dt>
                  <dd className="mt-1 text-sm font-semibold text-navy-900">{row.value}</dd>
                </div>
              ))}
            </dl>
          )}

          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6" data-testid="eco-service-pending">
            <p className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-600">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold-700" aria-hidden="true" />
              Full details for this service — inclusions, timescale, terms and pricing — are being
              finalised. Talk to us and we’ll confirm exactly what’s included and the right way to proceed.
            </p>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to={actionHref}
              data-testid="eco-service-action"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-navy-900 px-7 py-4 text-base font-bold text-white transition-colors hover:bg-navy-700"
            >
              {action.kind === 'details' ? 'Discuss This Service' : action.label}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
            </Link>
            <Link
              to="/ecosystem"
              className="inline-flex items-center justify-center gap-2 rounded-md border-2 border-navy-900 px-7 py-[14px] text-base font-bold text-navy-900 transition-colors hover:bg-navy-900 hover:text-white"
            >
              Browse More Solutions
            </Link>
          </div>

          {related.length > 0 && (
            <div className="mt-14">
              <h2 className="text-xl font-extrabold tracking-tight text-navy-900">Related solutions</h2>
              <ul className="mt-5 grid gap-4 sm:grid-cols-3">
                {related.map((rel) => (
                  <li key={rel.id}>
                    <Link
                      to={rel.href}
                      data-testid={`eco-related-${rel.slug}`}
                      className="block h-full rounded-xl border border-slate-200 bg-white p-5 transition-shadow duration-300 hover:shadow-card"
                    >
                      <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: division.dark }}>
                        {ITEM_TYPE_LABELS[rel.itemType]}
                      </span>
                      <span className="mt-2 block text-sm font-bold leading-snug text-navy-900">{rel.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
      <CtaBand
        eyebrow="Next step"
        heading="Not sure it’s the right fit?"
        copy="Tell us what you’re trying to achieve and we’ll help you choose the most practical option."
        primaryLabel="Discuss Your Needs"
        primaryHref="/?source=Ecosystem#solution-finder"
        secondaryLabel="Explore Services"
        secondaryHref="/services"
      />
    </>
  );
}
